import {Field, Formik} from 'formik';
import cx from 'classnames';
import {isEmptyOrNil, notEmptyOrNil} from '@ultradent/utilities/EmptyOrNil';
import Message from '../components/Message';
import {ShowTooltip} from '../components/Tooltip';
import {t} from '../util/i18n';
import React from 'react';
import {guid} from '@ultradent/utilities/Guid';
import Show from '../components/Show';

/**
 * Simple wrapper around Formik that uses the validate handler to broadcast "onChange" event that allows
 * the ability to track field states without the need of a form submission handler
 *
 * This component accepts the Formik interface
 * Any props passed are spread on the Formik component
 *
 * resource - https://jaredpalmer.com/formik/docs/api/formik
 */

export const TrackedFields = ( {onChange, ...props} ) => {
    return (
        <Formik
            validateOnChange={true}
            enableReinitialize={true}
            validate={( values ) => onChange && onChange( values )}
            {...props}
        />
    );
};

const NativeField = ( props ) => <input {...props} />;

export const FieldWrapper = ( {
                                  disabledTooltip,
                                  children,
                                  nativeField,
                                  ...props
                              } ) => {
    const InputField = nativeField ? NativeField : Field;
    return (
        <ShowTooltip tooltip={props.disabled && disabledTooltip} className="w-full">
            <InputField {...props}>{children}</InputField>
        </ShowTooltip>
    );
};

export const FieldLabel = ( {
                                id,
                                label,
                                hint,
                                tooltip,
                                error,
                                append,
                                className
                            } ) => (
    <div className="w-full flex justify-between items-end">
    <span>
        <Label htmlFor={id} error={error} className={cx( className )}>
            {label}
        </Label>
        <ShowTooltip tooltip={tooltip} useIcon/>
        <Hint htmlFor={id} error={error}>
            {hint}
        </Hint>
    </span>
    <Show when={append}>
        <span className="mb-1 leading-tight">{append}</span>
    </Show>
  </div>
);

export const Label = ( {error, className, children, ...props} ) => {
    return (
        <Show when={children}>
            <label className={cx( 'input-label', className )} {...props}>
                {children}
            </label>
        </Show>
    );
};

export const Hint = ( {error, className, children, ...props} ) => {
    return (
        <Show when={children}>
            <span className="input-hint mb-1 block text-caption text-base leading-tight" {...props}>
            {children}
            </span>
        </Show>
    );
};

export const InputFeedback = ( {error, className} ) =>
    error ? (
        <div className={cx( 'input-feedback error', className )}>
          {error.short || error}
        </div>
    ) : null;

const messageDefault = t(
    'message.genericFormError',
    'Please correct the following issues before continuing'
);

export const FormFeedback = ( {
                                  message = messageDefault,
                                  customMessage,
                                  errors = [],
                                  submitCount,
                                  className
                              } ) => {
    if ( (message === messageDefault && isEmptyOrNil( errors )) || submitCount < 1 ) {
        return null;
    }

    return (
        <Message
            fail
            className={`sticky top-16 z-10 ${className}`}
            onClick={( e ) => e.currentTarget.classList.replace( 'sticky', 'static' )}
        >
            <Show when={message}>
                <h3 className="font-medium text-base">{message}</h3>
            </Show>
            <Show when={notEmptyOrNil( errors )}>
                {Array.isArray( errors ) ? (
                    <ul className="bullets-x m-0 p-0 mt-2">
                        {errors.map( ( error ) => (
                            <li key={guid()} className="text-sm font-medium">
                                {error}
                            </li>
                        ) )}
                    </ul>
                ) : (
                    <div className="pl-4 mt-2">
            {Object.entries( errors ).map( ( [field, error] ) => (
                <label key={guid()} className="m-0 text-white block" htmlFor={field}>
                    -{' '}
                    <span className="inline hover:underline">
                  {error.long || error}
                </span>
                </label>
            ) )}
          </div>
                )}
            </Show>
            {customMessage}
        </Message>
    );
};
